import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface UseStepNavigationProps {
  cuit?: string;
  id?: string;
  moduleName: string;
  totalSteps: number;
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

interface StepProgress {
  maxStepReached: number;
  lastCompletedStep: number;
}

/**
 * Hook personalizado para manejar la navegación automática a pasos
 * basada en el progreso guardado en localStorage
 */
export const useStepNavigation = ({
  cuit,
  id,
  moduleName,
  totalSteps,
  currentStep,
  setCurrentStep,
}: UseStepNavigationProps) => {
  const { state } = useLocation();
  const [maxStepReached, setMaxStepReached] = useState<number>(1);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Clave única para localStorage basada en módulo, cuit e id
  const getStorageKey = () => {
    if (!cuit || !id) return null;
    return `${moduleName}_${cuit}_${id}_stepProgress`;
  };

  // Cargar progreso desde localStorage al inicializar
  useEffect(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      const savedProgress = localStorage.getItem(storageKey);
      if (savedProgress) {
        try {
          const progress: StepProgress = JSON.parse(savedProgress);
          setMaxStepReached(progress.maxStepReached);
          
          // Si no hay estado en la navegación (viene del listado), redirigir al último paso disponible
          if (!state) {
            const targetStep = Math.min(progress.maxStepReached + 1, totalSteps);
            setCurrentStep(targetStep);
          }
        } catch (error) {
          console.error('Error parsing step progress from localStorage:', error);
        }
      }
    }
    setIsInitialized(true);
  }, [cuit, id, moduleName, totalSteps, setCurrentStep, state]);

  // Guardar progreso en localStorage cuando cambie
  useEffect(() => {
    const storageKey = getStorageKey();
    if (storageKey && isInitialized && maxStepReached > 1) {
      const progress: StepProgress = {
        maxStepReached,
        lastCompletedStep: Math.max(1, maxStepReached - 1),
      };
      localStorage.setItem(storageKey, JSON.stringify(progress));
    }
  }, [maxStepReached, isInitialized, cuit, id, moduleName]);

  // Función para actualizar el paso máximo alcanzado
  const updateMaxStepReached = (newStep: number) => {
    setMaxStepReached((prev) => Math.max(prev, newStep));
  };

  // Función para navegar a un paso específico con validaciones
  const navigateToStep = (targetStep: number) => {
    // Permitir navegación si:
    // 1. El paso objetivo es menor o igual al máximo alcanzado
    // 2. El paso objetivo es el siguiente paso disponible
    // 3. El paso objetivo es el último paso (si todos están completos)
    const canNavigate = 
      targetStep <= maxStepReached + 1 || 
      targetStep <= currentStep ||
      targetStep === totalSteps;

    if (canNavigate) {
      setCurrentStep(targetStep);
    }
  };

  // Función para avanzar al siguiente paso
  const goToNextStep = () => {
    const nextStep = Math.min(currentStep + 1, totalSteps);
    setCurrentStep(nextStep);
    updateMaxStepReached(nextStep);
  };

  // Función para retroceder al paso anterior
  const goToPreviousStep = () => {
    const previousStep = Math.max(currentStep - 1, 1);
    setCurrentStep(previousStep);
  };

  // Función para limpiar el progreso (útil para testing o reset)
  const clearProgress = () => {
    const storageKey = getStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
      setMaxStepReached(1);
      setCurrentStep(1);
    }
  };

  return {
    maxStepReached,
    isInitialized,
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
    clearProgress,
  };
};

export default useStepNavigation;
